import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate, Link } from "react-router-dom";
import { User as UserIcon, Mail, Lock, Wrench, Save, MapPin, Calendar, Bell, Shield, Trash2, ExternalLink, AlertTriangle, Crown, CreditCard, RefreshCw, Gift, Star } from "lucide-react";
import LocationPicker from "@/components/LocationPicker";
import { PushOptIn } from "@/components/PushOptIn";
import { isNative, platform } from "@/lib/native";
import { getCustomerInfo } from "@/services/revenuecat";
import { passwordProblem } from "@/lib/passwordPolicy";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const DAYS = [
  { i: 0, n: "MON" }, { i: 1, n: "TUE" }, { i: 2, n: "WED" },
  { i: 3, n: "THU" }, { i: 4, n: "FRI" }, { i: 5, n: "SAT" }, { i: 6, n: "SUN" },
];

export default function Settings() {
  const { user, checkAuth, logout } = useAuth();
  const navigate = useNavigate();
  const _params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  const [tab, setTab] = useState(_params.get("tab") || "personal");
  const [profile, setProfile] = useState({
    name: "", email: "", shop_name: "", bio: "", specialties: "",
  });
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [loc, setLoc] = useState({ lat: null, lng: null, address: "", city: "", state: "", zip: "", radius_miles: 25 });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);
  const [savingLoc, setSavingLoc] = useState(false);

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || "",
        email: user.email || "",
        shop_name: user.shop_name || "",
        bio: user.bio || "",
        specialties: (user.specialties || []).join(", "),
      });
      const l = user.location || {};
      setLoc({
        lat: l.lat ?? null,
        lng: l.lng ?? null,
        address: l.address || "",
        city: l.city || "",
        state: l.state || "",
        zip: l.zip || "",
        radius_miles: user.radius_miles ?? 25,
      });
    }
  }, [user]);

  if (!user) return null;
  const isShop = user.role === "shop";
  const isOwner = user.role === "owner";
  const hasPassword = user.auth_provider === "jwt";

  const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.shiftquote.app";
  const handleRateApp = () => {
    // Analytics event: settings_rate_tap
    try { window.gtag?.("event", "settings_rate_tap"); } catch {}
    window.open(PLAY_STORE_URL, "_blank");
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const payload = { name: profile.name, email: profile.email };
      if (isShop) {
        payload.shop_name = profile.shop_name;
        payload.bio = profile.bio;
        payload.specialties = profile.specialties.split(",").map((s) => s.trim()).filter(Boolean);
      }
      await api.patch("/auth/profile", payload);
      await checkAuth();
      toast.success("Profile updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update"); }
    finally { setSavingProfile(false); }
  };

  const changePassword = async () => {
    const pwErr = passwordProblem(pwd.new_password);
    if (pwErr) { toast.error(pwErr); return; }
    if (pwd.new_password !== pwd.confirm) { toast.error("Passwords do not match"); return; }
    setSavingPwd(true);
    try {
      const payload = { new_password: pwd.new_password };
      if (hasPassword) payload.current_password = pwd.current_password;
      await api.post("/auth/password", payload);
      toast.success("Password updated");
      setPwd({ current_password: "", new_password: "", confirm: "" });
      await checkAuth();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSavingPwd(false); }
  };

  const saveLocation = async () => {
    if (loc.lat == null || loc.lng == null) { toast.error("Capture a location first"); return; }
    setSavingLoc(true);
    try {
      const payload = {
        lat: loc.lat, lng: loc.lng,
        address: loc.address, city: loc.city, state: loc.state, zip: loc.zip,
      };
      if (isOwner) payload.radius_miles = parseInt(loc.radius_miles) || 25;
      await api.patch("/auth/location", payload);
      await checkAuth();
      toast.success("Location saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSavingLoc(false); }
  };

  const tabs = [
    { k: "personal", label: "PERSONAL INFO", icon: UserIcon },
    { k: "email", label: "EMAIL", icon: Mail },
    { k: "password", label: "PASSWORD", icon: Lock },
    { k: "location", label: "LOCATION", icon: MapPin },
    ...(isShop ? [{ k: "shop", label: "SHOP DETAILS", icon: Wrench }, { k: "schedule", label: "SCHEDULE", icon: Calendar }, { k: "subscription", label: "SUBSCRIPTION", icon: Crown }] : []),
    ...(isOwner ? [{ k: "subscription", label: "SUBSCRIPTION", icon: Crown }] : []),
    ...(isShop || isOwner ? [{ k: "notifications", label: "NOTIFICATIONS", icon: Bell }] : []),
    { k: "rate", label: "RATE SHIFTQUOTE", icon: Star, action: true },
    { k: "privacy", label: "PRIVACY", icon: Shield },
  ];

  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />
      <main className="max-w-[1100px] mx-auto px-4 sm:px-8 lg:px-12 py-10" data-testid="settings-page">
        <div className="label-eyebrow mb-3">ACCOUNT SETTINGS</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-10">Settings.</h1>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <aside className="lg:col-span-3">
            <div className="border border-white/10 divide-y divide-white/10">
              {tabs.map((t) => {
                const Icon = t.icon;
                const active = tab === t.k;
                const isRate = t.k === "rate";
                return (
                  <button
                    key={t.k}
                    onClick={() => isRate ? handleRateApp() : setTab(t.k)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm transition-colors ${active ? "bg-[#FF3B30] text-white" : "bg-[#121214] text-zinc-400 hover:bg-white/5 hover:text-white"}`}
                    data-testid={`settings-tab-${t.k}`}
                  >
                    <Icon className={`w-4 h-4 ${isRate ? "text-yellow-400" : ""}`} strokeWidth={1.8} />
                    <div className="flex flex-col items-start">
                      <span className="uppercase tracking-widest text-xs font-medium">{t.label}</span>
                      {isRate && <span className="text-[10px] text-zinc-500 normal-case tracking-normal">Help fellow drivers find us</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          </aside>

          <section className="lg:col-span-9 border border-white/10 bg-[#121214] p-6 sm:p-10">
            {tab === "personal" && (
              <div className="space-y-5">
                <SectionHead title="Personal information" desc="Your display name. Your identity remains anonymous to shops via your handle." />
                <Field label="ACCOUNT NUMBER">
                  <div className="flex items-center gap-2">
                    <input value={user.account_number || "—"} disabled className="input-base opacity-80 font-mono tracking-widest" data-testid="settings-account-number" />
                    {user.account_number && (
                      <button
                        type="button"
                        onClick={() => { navigator.clipboard.writeText(user.account_number); toast.success("Copied"); }}
                        className="btn-ghost text-[10px] uppercase tracking-widest"
                        data-testid="copy-account-number"
                      >Copy</button>
                    )}
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-1.5">Share this with support when you need help.</p>
                </Field>
                <Field label="NAME">
                  <input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className="input-base" data-testid="settings-name-input" />
                </Field>
                {user.role === "owner" && (
                  <Field label="ANONYMOUS HANDLE">
                    <input value={user.anonymous_handle || ""} disabled className="input-base opacity-60" data-testid="settings-handle-input" />
                  </Field>
                )}
                <SaveBar onClick={saveProfile} loading={savingProfile} testid="settings-save-personal" />
              </div>
            )}

            {tab === "email" && (
              <div className="space-y-5">
                <SectionHead title="Email address" desc="Used for sign-in and important account notifications." />
                <Field label="EMAIL">
                  <input type="email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} className="input-base" data-testid="settings-email-input" />
                </Field>
                <SaveBar onClick={saveProfile} loading={savingProfile} testid="settings-save-email" />
              </div>
            )}

            {tab === "password" && (
              <div className="space-y-5">
                <SectionHead title="Password" desc={hasPassword ? "Change your sign-in password." : "Set a password to enable email/password login. (You're currently signed in via Google.)"} />
                {hasPassword && (
                  <Field label="CURRENT PASSWORD">
                    <input type="password" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} className="input-base" data-testid="settings-current-password" />
                  </Field>
                )}
                <Field label="NEW PASSWORD">
                  <input type="password" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} className="input-base" data-testid="settings-new-password" />
                </Field>
                <Field label="CONFIRM NEW PASSWORD">
                  <input type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} className="input-base" data-testid="settings-confirm-password" />
                </Field>
                <div className="pt-2">
                  <button onClick={changePassword} disabled={savingPwd} className="btn-primary flex items-center gap-2" data-testid="settings-save-password">
                    <Save className="w-4 h-4" /> {savingPwd ? "Updating..." : hasPassword ? "Update password" : "Set password"}
                  </button>
                </div>
              </div>
            )}

            {tab === "location" && (
              <div className="space-y-5">
                <SectionHead
                  title={isOwner ? "Location & search radius" : "Shop location"}
                  desc={isOwner ? "Only shops within your chosen radius of this location will see your jobs by default." : "Pin your shop on the map. Owner radius searches use this location."}
                />
                <LocationPicker
                  lat={loc.lat}
                  lng={loc.lng}
                  onChange={(lat, lng) => setLoc((l) => ({ ...l, lat, lng }))}
                  radiusMiles={isOwner ? loc.radius_miles : 0}
                  showRadius={isOwner}
                  height="320px"
                  testid="settings-location-picker"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="ADDRESS">
                    <input value={loc.address} onChange={(e) => setLoc({ ...loc, address: e.target.value })} className="input-base" data-testid="settings-addr" />
                  </Field>
                  <Field label="CITY">
                    <input value={loc.city} onChange={(e) => setLoc({ ...loc, city: e.target.value })} className="input-base" data-testid="settings-city" />
                  </Field>
                  <Field label="STATE">
                    <input value={loc.state} onChange={(e) => setLoc({ ...loc, state: e.target.value })} className="input-base" data-testid="settings-state" />
                  </Field>
                  <Field label="ZIP">
                    <input value={loc.zip} onChange={(e) => setLoc({ ...loc, zip: e.target.value })} className="input-base" data-testid="settings-zip" />
                  </Field>
                </div>
                {isOwner && (
                  <Field label={`DEFAULT SEARCH RADIUS — ${loc.radius_miles} MI`}>
                    <input type="range" min="5" max="100" step="5" value={loc.radius_miles} onChange={(e) => setLoc({ ...loc, radius_miles: parseInt(e.target.value) })} className="w-full accent-[#FF3B30]" data-testid="settings-radius-slider" />
                  </Field>
                )}
                <div className="pt-2">
                  <button onClick={saveLocation} disabled={savingLoc} className="btn-primary flex items-center gap-2" data-testid="settings-save-location">
                    <Save className="w-4 h-4" /> {savingLoc ? "Saving..." : "Save location"}
                  </button>
                </div>
              </div>
            )}

            {tab === "shop" && isShop && (
              <div className="space-y-5">
                <SectionHead title="Shop details" desc="Public information shown to car owners reviewing your quotes." />
                <Field label="SHOP NAME">
                  <input value={profile.shop_name} onChange={(e) => setProfile({ ...profile, shop_name: e.target.value })} className="input-base" data-testid="settings-shop-name" />
                </Field>
                <Field label="BIO">
                  <textarea rows={4} value={profile.bio} onChange={(e) => setProfile({ ...profile, bio: e.target.value })} className="input-base" data-testid="settings-bio" />
                </Field>
                <Field label="SPECIALTIES (comma separated)">
                  <input value={profile.specialties} onChange={(e) => setProfile({ ...profile, specialties: e.target.value })} className="input-base" placeholder="Brakes, Transmission, EVs" data-testid="settings-specialties" />
                </Field>
                <SaveBar onClick={saveProfile} loading={savingProfile} testid="settings-save-shop" />
              </div>
            )}

            {tab === "schedule" && isShop && <ScheduleEditor />}
            {tab === "subscription" && isShop && <SubscriptionTab user={user} />}
            {tab === "subscription" && isOwner && <OwnerSubscriptionTab user={user} />}
            {tab === "notifications" && (isShop || isOwner) && (
              <div className="space-y-5">
                <SectionHead title="Notifications" desc={isOwner ? "Choose how ShiftQuote can ping you when a shop sends a private quote or message." : "Choose how ShiftQuote can ping you when there's a new job nearby."} />
                <PushOptIn />
                {isShop && <ShopNotificationToggles />}
              </div>
            )}
            {tab === "privacy" && (
              <PrivacyTab user={user} onDeleted={async () => {
                await logout();
                navigate("/", { replace: true });
              }} />
            )}
          </section>
        </div>

        {/* ── Log out ── */}
        <div className="max-w-3xl mx-auto px-6 pb-10">
          <button
            type="button"
            onClick={async () => {
              try { await logout(); } catch { /* ignore */ }
              try { localStorage.removeItem("admin_token"); } catch { /* ignore */ }
              window.location.href = "/login";
            }}
            className="w-full inline-flex items-center justify-center gap-2 border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors px-4 py-3 text-xs uppercase tracking-widest font-medium"
            data-testid="settings-logout-btn"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            Log out
          </button>
        </div>
      </main>
    </div>
  );
}

function ScheduleEditor() {
  const [sched, setSched] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/shop/schedule");
        setSched(res.data);
      } catch (e) { toast.error("Failed to load schedule"); }
      finally { setLoading(false); }
    })();
  }, []);

  const update = (idx, key, value) => {
    setSched((s) => ({ ...s, weekly: s.weekly.map((d, i) => i === idx ? { ...d, [key]: value } : d) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/shop/schedule", sched);
      toast.success("Schedule saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  if (loading || !sched) return <div className="text-zinc-500 text-sm">Loading...</div>;

  // Make sure all 7 days present
  const byDay = new Map(sched.weekly.map((d) => [d.weekday, d]));
  const weekly = DAYS.map((d) => byDay.get(d.i) || { weekday: d.i, enabled: false, open: "09:00", close: "17:00" });

  return (
    <div className="space-y-5">
      <SectionHead title="Weekly schedule" desc="Owners book appointments inside this window. You can override per-quote later." />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="TIMEZONE">
          <input value={sched.timezone || ""} onChange={(e) => setSched({ ...sched, timezone: e.target.value })} className="input-base" placeholder="America/Los_Angeles" data-testid="sched-tz" />
        </Field>
        <Field label="SLOT LENGTH (MINUTES)">
          <select value={sched.slot_minutes} onChange={(e) => setSched({ ...sched, slot_minutes: parseInt(e.target.value) })} className="input-base" data-testid="sched-slot">
            {[15, 30, 45, 60, 90, 120, 180, 240].map((n) => <option key={n} value={n}>{n} min</option>)}
          </select>
        </Field>
      </div>

      <div className="border border-white/10 bg-[#09090B] divide-y divide-white/10">
        {weekly.map((d, idx) => (
          <div key={d.weekday} className="grid grid-cols-12 gap-3 items-center px-4 py-3" data-testid={`sched-day-${d.weekday}`}>
            <div className="col-span-3 sm:col-span-2 label-eyebrow text-white">{DAYS[idx].n}</div>
            <label className="col-span-3 sm:col-span-2 flex items-center gap-2 text-xs uppercase tracking-widest cursor-pointer">
              <input type="checkbox" checked={!!d.enabled} onChange={(e) => {
                setSched((s) => ({ ...s, weekly: weekly.map((wd, i) => i === idx ? { ...wd, enabled: e.target.checked } : wd) }));
              }} className="accent-[#FF3B30]" data-testid={`sched-enabled-${d.weekday}`} />
              <span className={d.enabled ? "text-[#34C759]" : "text-zinc-500"}>{d.enabled ? "OPEN" : "CLOSED"}</span>
            </label>
            <input type="time" disabled={!d.enabled} value={d.open} onChange={(e) => {
              setSched((s) => ({ ...s, weekly: weekly.map((wd, i) => i === idx ? { ...wd, open: e.target.value } : wd) }));
            }} className="input-base col-span-3 sm:col-span-3 disabled:opacity-40" data-testid={`sched-open-${d.weekday}`} />
            <span className="col-span-1 text-center text-zinc-600">—</span>
            <input type="time" disabled={!d.enabled} value={d.close} onChange={(e) => {
              setSched((s) => ({ ...s, weekly: weekly.map((wd, i) => i === idx ? { ...wd, close: e.target.value } : wd) }));
            }} className="input-base col-span-2 sm:col-span-3 disabled:opacity-40" data-testid={`sched-close-${d.weekday}`} />
          </div>
        ))}
      </div>

      <Field label="BLACKOUT DATES (comma separated, YYYY-MM-DD)">
        <input
          value={(sched.blackout_dates || []).join(", ")}
          onChange={(e) => setSched({ ...sched, blackout_dates: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
          className="input-base"
          placeholder="2026-07-04, 2026-12-25"
          data-testid="sched-blackout"
        />
      </Field>

      <div className="pt-2">
        <button onClick={() => { setSched((s) => ({ ...s, weekly })); save(); }} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="settings-save-schedule">
          <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save schedule"}
        </button>
      </div>
    </div>
  );
}

function SubscriptionTab({ user }) {
  const [subState, setSubState] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const tier = user?.subscription_tier || "free";
  const isPaid = tier === "pro" || tier === "premium";
  const trialUntil = user?.welcome_trial_until;
  const premiumUntil = user?.premium_until;
  const isOnTrial = !!(isPaid && trialUntil && premiumUntil && new Date(premiumUntil).getTime() <= new Date(trialUntil).getTime() + 60_000);
  const trialDaysLeft = isOnTrial && trialUntil ? Math.max(0, Math.ceil((new Date(trialUntil).getTime() - Date.now()) / 86_400_000)) : 0;
  const tierLabel = tier === "premium" ? "Premium" : tier === "pro" ? "Pro" : "Free";
  const tierPrice = tier === "premium" ? "$79.99/mo" : tier === "pro" ? "$59.99/mo" : "$0/mo";
  const tierColor = tier === "premium" ? "#FF3B30" : tier === "pro" ? "#FF9500" : "#71717a";
  const tierDescription = tier === "premium" ? "Unlimited concurrent jobs" : tier === "pro" ? "3 concurrent active jobs" : "1 active job at a time";

  const trialUntilDate = user?.welcome_trial_until ? new Date(user.welcome_trial_until) : null;
  const isFoundingTrial = !!(trialUntilDate && trialUntilDate > new Date() && user?.subscription_tier === "premium");
  const foundingDaysLeft = isFoundingTrial ? Math.max(0, Math.ceil((trialUntilDate.getTime() - Date.now()) / 86_400_000)) : 0;

  useEffect(() => {
    api.get("/billing/subscription")
      .then((res) => setSubState(res.data))
      .catch(() => setSubState(null))
      .finally(() => setLoading(false));
  }, []);

  const onOpenPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await api.post("/billing/portal", { origin_url: window.location.origin });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Portal unavailable");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open billing portal");
    } finally {
      setPortalLoading(false);
    }
  };

  if (loading) return <div className="text-zinc-500 text-sm">Loading subscription...</div>;

  const hasStripeSub = !!subState?.stripe_subscription_id;
  const cancelAtPeriodEnd = subState?.cancel_at_period_end;
  const stripeStatus = subState?.status;

  return (
    <div className="space-y-6" data-testid="settings-subscription-tab">
      <SectionHead title="Subscription" desc="Your current plan, billing status, and upgrade options." />

      {/* Current plan card */}
      {isFoundingTrial ? (
        <div className="rounded-xl border border-green-700/50 bg-green-950/40 p-5">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-green-400 text-xl">🎁</span>
            <span className="text-base font-bold text-green-400">Founding Member</span>
          </div>
          <p className="text-sm text-green-300 font-medium">Full Premium access — {foundingDaysLeft} day{foundingDaysLeft !== 1 ? "s" : ""} remaining</p>
          <p className="mt-2 text-xs text-zinc-400">
            As a founding shop you have free Premium for 120 days. Once your period ends, you can subscribe to keep your Premium benefits.
          </p>
          <button
            disabled
            className="mt-4 w-full cursor-not-allowed rounded-lg bg-zinc-800 py-3 text-sm font-semibold text-zinc-500 opacity-60"
          >
            🔒 Upgrade available in {foundingDaysLeft} day{foundingDaysLeft !== 1 ? "s" : ""}
          </button>
        </div>
      ) : (
        <div className="border border-white/10 bg-[#09090B] p-6">
          <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 flex items-center justify-center" style={{ background: `${tierColor}1a`, border: `1px solid ${tierColor}66` }}>
                <Crown className="w-6 h-6" style={{ color: tierColor }} />
              </div>
              <div>
                <div className="label-eyebrow text-zinc-500 mb-1">CURRENT PLAN</div>
                <div className="flex items-baseline gap-2">
                  <h3 className="font-heading text-2xl font-bold" style={{ color: tierColor }}>{tierLabel}</h3>
                  <span className="text-sm text-zinc-400">{tierPrice}</span>
                </div>
              </div>
            </div>
            {isOnTrial && (
              <div className="inline-flex items-center gap-1.5 border border-[#FF9500]/40 bg-[#FF9500]/5 text-[#FF9500] text-[10px] uppercase tracking-widest px-2.5 py-1" data-testid="sub-trial-badge">
                <Gift className="w-3 h-3" /> Trial · {trialDaysLeft}d left
              </div>
            )}
            {hasStripeSub && stripeStatus && !isOnTrial && (
              <span className={`text-[10px] uppercase tracking-widest px-2.5 py-1 border ${stripeStatus === "active" ? "border-[#34C759]/40 bg-[#34C759]/5 text-[#34C759]" : stripeStatus === "past_due" ? "border-[#FF9500]/40 bg-[#FF9500]/5 text-[#FF9500]" : "border-white/10 text-zinc-500"}`} data-testid="sub-status-badge">
                {stripeStatus}
              </span>
            )}
          </div>

          <p className="text-sm text-zinc-400 mb-4">{tierDescription}</p>

          {/* Renewal / trial info */}
          {isPaid && premiumUntil && (
            <div className="text-xs text-zinc-500 font-mono mb-4" data-testid="sub-renewal-info">
              {isOnTrial
                ? `Trial ends ${new Date(premiumUntil).toLocaleDateString()} · Auto-renews at ${tierPrice}`
                : cancelAtPeriodEnd
                  ? `Access until ${new Date(premiumUntil).toLocaleDateString()} · Cancellation pending`
                  : `Renews ${new Date(premiumUntil).toLocaleDateString()}`}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-2">
            {!isPaid && (
              <button
                onClick={() => navigate("/upgrade")}
                className="btn-primary flex items-center gap-2"
                data-testid="sub-upgrade-btn"
              >
                <Crown className="w-4 h-4" /> Upgrade plan
              </button>
            )}
            {isPaid && (
              <>
                <button
                  onClick={() => navigate("/upgrade")}
                  className="btn-secondary flex items-center gap-2"
                  data-testid="sub-change-plan-btn"
                >
                  <RefreshCw className="w-4 h-4" /> Change plan
                </button>
                {hasStripeSub && (
                  <button
                    onClick={onOpenPortal}
                    disabled={portalLoading}
                    className="btn-primary flex items-center gap-2"
                    data-testid="sub-manage-btn"
                  >
                    <CreditCard className={`w-4 h-4 ${portalLoading ? "animate-pulse" : ""}`} />
                    {portalLoading ? "Opening Stripe..." : "Manage subscription"}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Help / FAQ-like blurb */}
      <div className="border border-white/10 bg-[#09090B] p-5 text-xs text-zinc-400 space-y-2" data-testid="sub-help">
        <div className="label-eyebrow text-white mb-1">WHAT YOU CAN DO IN THE STRIPE PORTAL</div>
        <ul className="space-y-1.5 pl-4 list-disc">
          <li>Update your payment method (credit card, etc.)</li>
          <li>Download invoices & receipts for accounting</li>
          <li>Cancel your subscription — access continues until the end of the current billing period</li>
          <li>Change billing email or business name</li>
        </ul>
      </div>
    </div>
  );
}

// Car owners pay ONLY through native IAP (Apple / Google Play via
// RevenueCat), so their billing row links to the store's subscription
// management page, never the Stripe portal. Legacy web-Stripe owner subs
// (no premium_source="revenuecat" but a Stripe customer) still get the portal.
const APPLE_SUBS_URL = "https://apps.apple.com/account/subscriptions";
const PLAY_SUBS_URL = "https://play.google.com/store/account/subscriptions";

function OwnerSubscriptionTab({ user }) {
  const navigate = useNavigate();
  const [opening, setOpening] = useState(false);
  const isPremium = user?.subscription_tier === "owner_premium";
  const premiumUntil = user?.premium_until;
  const billingIssue = !!user?.billing_issue;
  const native = isNative();
  const isIOS = platform() === "ios";
  const stripeManaged = isPremium && user?.premium_source !== "revenuecat" && !!user?.stripe_customer_id;

  const openExternal = (url) => {
    if (native) window.open(url, "_system");
    else window.open(url, "_blank", "noopener,noreferrer");
  };

  const onManageStore = async () => {
    setOpening(true);
    try {
      let url = null;
      try {
        const info = await getCustomerInfo();
        url = info?.managementURL || null;
      } catch (_) { /* fall back to the platform URL */ }
      openExternal(url || (isIOS ? APPLE_SUBS_URL : PLAY_SUBS_URL));
    } finally {
      setOpening(false);
    }
  };

  const onManageStripe = async () => {
    setOpening(true);
    try {
      const res = await api.post("/billing/portal", { origin_url: window.location.origin });
      if (res.data?.url) window.location.href = res.data.url;
      else toast.error("Portal unavailable");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't open billing portal");
    } finally {
      setOpening(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-owner-subscription-tab">
      <SectionHead title="Subscription" desc="Your current plan and billing." />

      <div className="border border-white/10 bg-[#09090B] p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 flex items-center justify-center bg-[#FFB800]/10 border border-[#FFB800]/40">
            <Crown className="w-6 h-6 text-[#FFB800]" />
          </div>
          <div>
            <div className="label-eyebrow text-zinc-500 mb-1">CURRENT PLAN</div>
            <div className="flex items-baseline gap-2">
              <h3 className={`font-heading text-2xl font-bold ${isPremium ? "text-[#FFB800]" : "text-zinc-300"}`}>{isPremium ? "Premium" : "Free"}</h3>
              <span className="text-sm text-zinc-400">{isPremium ? "$3.99/mo" : "$0/mo"}</span>
            </div>
          </div>
        </div>

        <p className="text-sm text-zinc-400 mb-4">
          {isPremium
            ? "Auto-Track, Receipt Vault and Profit Radar."
            : "Quick Trip mileage logging and repair quotes."}
        </p>

        {isPremium && premiumUntil && (
          <div className="text-xs text-zinc-500 font-mono mb-4" data-testid="owner-sub-renewal-info">
            Current period ends {new Date(premiumUntil).toLocaleDateString()}
          </div>
        )}

        {billingIssue && (
          <div className="flex items-start gap-2 border border-[#FF9500]/40 bg-[#FF9500]/5 text-[#FF9500] text-xs p-3 mb-4" data-testid="owner-sub-billing-issue">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>There was a problem with your last payment. Update your payment method in your {isIOS ? "Apple ID" : "Google Play"} settings to keep Premium.</span>
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-2">
          {!isPremium && (
            <button onClick={() => navigate("/owner/upgrade")} className="btn-primary flex items-center gap-2" data-testid="owner-sub-upgrade-btn">
              <Crown className="w-4 h-4" /> Upgrade to Premium
            </button>
          )}
          {isPremium && stripeManaged && (
            <button onClick={onManageStripe} disabled={opening} className="btn-primary flex items-center gap-2" data-testid="owner-sub-manage-stripe-btn">
              <CreditCard className={`w-4 h-4 ${opening ? "animate-pulse" : ""}`} />
              {opening ? "Opening..." : "Manage billing"}
            </button>
          )}
          {isPremium && !stripeManaged && native && (
            <button onClick={onManageStore} disabled={opening} className="btn-primary flex items-center gap-2" data-testid="owner-sub-manage-store-btn">
              <ExternalLink className={`w-4 h-4 ${opening ? "animate-pulse" : ""}`} />
              {opening ? "Opening..." : `Manage in ${isIOS ? "App Store" : "Google Play"}`}
            </button>
          )}
          {isPremium && !stripeManaged && !native && (
            <>
              <button onClick={() => openExternal(APPLE_SUBS_URL)} className="btn-secondary flex items-center gap-2" data-testid="owner-sub-manage-apple-link">
                <ExternalLink className="w-4 h-4" /> Manage in App Store
              </button>
              <button onClick={() => openExternal(PLAY_SUBS_URL)} className="btn-secondary flex items-center gap-2" data-testid="owner-sub-manage-play-link">
                <ExternalLink className="w-4 h-4" /> Manage in Google Play
              </button>
            </>
          )}
        </div>
      </div>

      {!stripeManaged && (
        <div className="border border-white/10 bg-[#09090B] p-5 text-xs text-zinc-400 space-y-2" data-testid="owner-sub-help">
          <div className="label-eyebrow text-white mb-1">BILLED BY {native ? (isIOS ? "APPLE" : "GOOGLE PLAY") : "APPLE OR GOOGLE PLAY"}</div>
          <ul className="space-y-1.5 pl-4 list-disc">
            <li>Cancel or change your plan from your {native ? (isIOS ? "App Store" : "Google Play") : "App Store or Google Play"} subscriptions page</li>
            <li>Access continues until the end of the current billing period after cancelling</li>
            <li>Deleting your ShiftQuote account does not cancel a store subscription; cancel it in the store first</li>
          </ul>
        </div>
      )}
    </div>
  );
}

function SectionHead({ title, desc }) {
  return (
    <div className="border-b border-white/10 pb-4 mb-2">
      <h2 className="font-heading text-2xl font-bold tracking-tight">{title}</h2>
      {desc && <p className="text-sm text-zinc-500 mt-1">{desc}</p>}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label-eyebrow block mb-2">{label}</label>
      {children}
    </div>
  );
}

function SaveBar({ onClick, loading, testid }) {
  return (
    <div className="pt-2">
      <button onClick={onClick} disabled={loading} className="btn-primary flex items-center gap-2" data-testid={testid}>
        <Save className="w-4 h-4" /> {loading ? "Saving..." : "Save changes"}
      </button>
    </div>
  );
}


function ShopNotificationToggles() {
  const [prefs, setPrefs] = useState({ email_notifications: true, sms_notifications: true });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/shop/notification-preferences");
        if (res.data) setPrefs(res.data);
      } catch {}
      setLoading(false);
    })();
  }, []);

  const toggle = async (field) => {
    const newVal = !prefs[field];
    setPrefs((p) => ({ ...p, [field]: newVal }));
    try {
      await api.patch("/shop/notification-preferences", { [field]: newVal });
      toast.success(`${field === "email_notifications" ? "Email" : "SMS"} notifications ${newVal ? "enabled" : "disabled"}`);
    } catch {
      setPrefs((p) => ({ ...p, [field]: !newVal }));
      toast.error("Failed to update preference");
    }
  };

  if (loading) return null;

  return (
    <div className="space-y-4 mt-6 border-t border-white/10 pt-6">
      <h4 className="text-xs font-bold uppercase tracking-widest text-zinc-500">Lead Notifications</h4>
      <div className="space-y-3">
        <label className="flex items-center justify-between p-3 bg-zinc-800/50 rounded border border-white/5 cursor-pointer">
          <div>
            <div className="text-sm font-medium text-white">Email notifications</div>
            <div className="text-xs text-zinc-500">Get emailed when a driver posts a repair request near you</div>
          </div>
          <button
            onClick={() => toggle("email_notifications")}
            className={`relative w-11 h-6 rounded-full transition-colors ${prefs.email_notifications ? "bg-emerald-500" : "bg-zinc-700"}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${prefs.email_notifications ? "translate-x-5" : ""}`} />
          </button>
        </label>
        <label className="flex items-center justify-between p-3 bg-zinc-800/50 rounded border border-white/5 cursor-pointer">
          <div>
            <div className="text-sm font-medium text-white">SMS notifications</div>
            <div className="text-xs text-zinc-500">Get a text message for new leads (founding & paid shops only)</div>
          </div>
          <button
            onClick={() => toggle("sms_notifications")}
            className={`relative w-11 h-6 rounded-full transition-colors ${prefs.sms_notifications ? "bg-emerald-500" : "bg-zinc-700"}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${prefs.sms_notifications ? "translate-x-5" : ""}`} />
          </button>
        </label>
      </div>
    </div>
  );
}

function PrivacyTab({ user, onDeleted }) {
  const { logout } = useAuth();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const isAdmin = user?.role === "admin";

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete("/auth/me");
      toast.success("Your account has been deleted.");
      setConfirmOpen(false);
      await onDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete account");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="settings-privacy-tab">
      <SectionHead
        title="Privacy & data"
        desc="Manage how your data is used and remove your account permanently."
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Link
          to="/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="border border-white/10 bg-[#09090B] hover:border-white/30 transition-colors p-5 flex items-start justify-between gap-3 group"
          data-testid="settings-privacy-link"
        >
          <div>
            <div className="label-eyebrow text-white mb-1">PRIVACY POLICY</div>
            <p className="text-xs text-zinc-500">What we collect, how we use it, and your rights.</p>
          </div>
          <ExternalLink className="w-4 h-4 text-zinc-500 group-hover:text-[#FF3B30] flex-shrink-0" />
        </Link>
        <Link
          to="/terms"
          target="_blank"
          rel="noopener noreferrer"
          className="border border-white/10 bg-[#09090B] hover:border-white/30 transition-colors p-5 flex items-start justify-between gap-3 group"
          data-testid="settings-terms-link"
        >
          <div>
            <div className="label-eyebrow text-white mb-1">TERMS OF SERVICE</div>
            <p className="text-xs text-zinc-500">The rules of using ShiftQuote.</p>
          </div>
          <ExternalLink className="w-4 h-4 text-zinc-500 group-hover:text-[#FF3B30] flex-shrink-0" />
        </Link>
      </div>


      <div className="border border-[#FF3B30]/40 bg-[#FF3B30]/[0.04] p-6">
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle className="w-5 h-5 text-[#FF3B30] flex-shrink-0 mt-0.5" strokeWidth={1.8} />
          <div>
            <h3 className="font-heading text-lg font-bold text-white mb-1">Delete account</h3>
            <p className="text-sm text-zinc-400">
              Permanently delete your ShiftQuote account. We&apos;ll cancel any active Stripe subscription, wipe your private data, and anonymize historical records. Subscriptions bought through the App Store or Google Play must be cancelled in the store. This cannot be undone.
            </p>
          </div>
        </div>
        {isAdmin ? (
          <p className="text-xs text-zinc-500 pl-8">
            Admin accounts cannot be self-deleted. Ask another admin to remove your account from the Admin console.
          </p>
        ) : (
          <button
            type="button"
            onClick={() => { setConfirmText(""); setConfirmOpen(true); }}
            className="ml-8 inline-flex items-center gap-2 border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white transition-colors px-4 py-2 text-xs uppercase tracking-widest font-medium"
            data-testid="settings-delete-account-btn"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete my account
          </button>
        )}
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-[#0f0f12] border-white/10 text-zinc-100" data-testid="delete-account-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-2xl text-white">Delete your account?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-zinc-400 space-y-2">
              <span className="block">This will:</span>
              <span className="block">• Cancel any active Premium subscription on Stripe</span>
              <span className="block">• Delete your push tokens, payment history, saved templates, mileage, receipts and vehicles</span>
              <span className="block">• {user?.role === "owner" ? "Delete all your posted issues and their photos/videos" : "Delete all your submitted bids and bookings"}</span>
              <span className="block">• Anonymize your messages so the other party&apos;s thread still works</span>
              <span className="block pt-2 text-[#FFB800]">App Store / Google Play subscriptions are billed by Apple or Google and are NOT cancelled automatically. Cancel them in your store subscription settings.</span>
              <span className="block pt-2 text-[#FF3B30]">This action is permanent and cannot be reversed.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <label className="label-eyebrow block mb-2">Type <span className="text-[#FF3B30]">DELETE</span> to confirm</label>
            <input
              autoFocus
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="input-base font-mono"
              placeholder="DELETE"
              data-testid="delete-account-confirm-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-white/10 bg-transparent text-zinc-300 hover:bg-white/5 hover:text-white"
              data-testid="delete-account-cancel"
              disabled={deleting}
            >Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={confirmText !== "DELETE" || deleting}
              onClick={(e) => { e.preventDefault(); handleDelete(); }}
              className="bg-[#FF3B30] text-white hover:bg-[#FF3B30]/90 disabled:opacity-40 disabled:pointer-events-none"
              data-testid="delete-account-confirm-btn"
            >
              {deleting ? "Deleting..." : "Delete forever"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
